import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type {
  JansenHuybregtsAopTicketStatusOptionDto,
  JansenHuybregtsProjectConfigurationDto,
  JansenHuybregtsTicketStatusOptionsDto,
  UserDto,
} from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { Accordion } from "@/design-system/Accordion";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

type JansenHuybregtsFormValues = {
  enabled: boolean;
  baseUrl: string;
  apiKey: string;
  fallbackUser: UserDto | null;
  assigneeUser: UserDto | null;
  requesterUser: UserDto | null;
  ignoreCategories: boolean;
  statusMappings: {
    externalStatusId: string;
    aopStatus: JansenHuybregtsAopTicketStatusOptionDto | undefined;
  }[];
};

export function JansenHuybregtsPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const canManage = hasPermission((x) => x.integrations.canManageJansenHuybregts);
  const integrationQueries = useIntegrationQueries();
  const userQueries = useUserQueries();

  const {
    data: configuration,
    isFetching: isLoadingConfiguration,
    error: configurationError,
  } = useQuery({ ...integrationQueries.jansenHuybregtsConfiguration(), enabled: canManage });
  const {
    data: adminUsers,
    isFetching: isLoadingUsers,
    error: usersError,
  } = useQuery({ ...userQueries.listAdmins(), enabled: canManage });
  const {
    data: ticketStatusOptions,
    isFetching: isLoadingTicketStatusOptions,
    error: ticketStatusOptionsError,
  } = useQuery({ ...integrationQueries.jansenHuybregtsTicketStatusOptions(), enabled: canManage });

  const mutation = integrationMutations.useEditJansenHuybregtsConfiguration();

  if (!canManage) {
    return <ErrorPage status={404} />;
  }

  if (configurationError || usersError || ticketStatusOptionsError) {
    return <ErrorPage error={configurationError || usersError || ticketStatusOptionsError} />;
  }

  if (isLoadingConfiguration || isLoadingUsers || isLoadingTicketStatusOptions) {
    return <FullSizeLoader withPadding />;
  }

  const users = adminUsers ?? [];
  const options = ticketStatusOptions ?? { aopStatuses: [], externalStatuses: [] };
  const name = t("page.integration.jansen-huybregts.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <JansenHuybregtsForm
        defaultValues={{
          enabled: configuration?.enabled ?? false,
          baseUrl: configuration?.baseUrl ?? "",
          apiKey: configuration?.apiKey ?? "",
          fallbackUser: users.find((user) => user.id === configuration?.fallbackUserId) ?? null,
          assigneeUser: users.find((user) => user.id === configuration?.assigneeUserId) ?? null,
          requesterUser: users.find((user) => user.id === configuration?.requesterUserId) ?? null,
          ignoreCategories: configuration?.ignoreCategories ?? false,
          statusMappings: options.externalStatuses.map((externalStatus) => {
            const savedMapping = configuration?.statusMappings?.find(
              (mapping) => mapping.externalStatus === externalStatus.value,
            );

            return {
              externalStatusId: externalStatus.value,
              aopStatus: savedMapping
                ? options.aopStatuses.find((status) => status.id === savedMapping.ticketStatusId)
                : undefined,
            };
          }),
        }}
        adminUsers={users}
        ticketStatusOptions={options}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

type JansenHuybregtsFormProps = {
  defaultValues: JansenHuybregtsFormValues;
  adminUsers: UserDto[];
  ticketStatusOptions: JansenHuybregtsTicketStatusOptionsDto;
  isSubmitting: boolean;
  onSubmit: (data: JansenHuybregtsProjectConfigurationDto) => void;
};

function JansenHuybregtsForm({
  defaultValues,
  adminUsers,
  ticketStatusOptions,
  isSubmitting,
  onSubmit,
}: JansenHuybregtsFormProps): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<JansenHuybregtsFormValues>({ defaultValues });
  const enabled = useWatch({ control: formMethods.control, name: "enabled" });

  const handleSubmit = (formValues: JansenHuybregtsFormValues) => {
    onSubmit({
      enabled: formValues.enabled,
      baseUrl: formValues.baseUrl,
      apiKey: formValues.apiKey,
      fallbackUserId: formValues.fallbackUser?.id ?? "",
      assigneeUserId: formValues.assigneeUser?.id,
      requesterUserId: formValues.requesterUser?.id,
      ignoreCategories: formValues.ignoreCategories,
      statusMappings: formValues.statusMappings
        .filter((mapping) => mapping.aopStatus)
        .map((mapping) => ({
          externalStatus: mapping.externalStatusId,
          ticketStatusId: mapping.aopStatus!.id,
        })),
    });
  };

  return (
    <Form.Root formMethods={formMethods} onSubmit={handleSubmit}>
      <Form.Container title={t("page.integration.jansen-huybregts.title")}>
        <Form.Body>
          <FormCheckbox<JansenHuybregtsFormValues, "enabled">
            name="enabled"
            label={t("page.integration.jansen-huybregts.form.enabled")}
            tooltip={t("page.integration.jansen-huybregts.form.enabled.tooltip")}
            onChange={(event) => {
              if (!event.target.checked) {
                formMethods.clearErrors();
              }
            }}
          />

          <FormField
            label={t("page.integration.jansen-huybregts.form.base-url")}
            tooltip={t("page.integration.jansen-huybregts.form.base-url.tooltip")}
            required={enabled}
          >
            <FormInput<JansenHuybregtsFormValues, "baseUrl">
              name="baseUrl"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.jansen-huybregts.form.base-url")
                    : () => undefined,
                },
              }}
              placeholder="https://api.jansenhuybregts.nl"
              disabled={!enabled}
            />
          </FormField>

          <FormField
            label={t("page.integration.jansen-huybregts.form.api-key")}
            tooltip={t("page.integration.jansen-huybregts.form.api-key.tooltip")}
            required={enabled}
          >
            <FormInput<JansenHuybregtsFormValues, "apiKey">
              name="apiKey"
              type="password"
              rules={{
                validate: {
                  required: enabled
                    ? createRequiredStringRule(t, "page.integration.jansen-huybregts.form.api-key")
                    : () => undefined,
                },
              }}
              placeholder="••••••••"
              disabled={!enabled}
            />
          </FormField>

          <FormField
            label={t("page.integration.jansen-huybregts.form.fallback-user")}
            description={t("page.integration.jansen-huybregts.form.fallback-user.description")}
            tooltip={t("page.integration.jansen-huybregts.form.fallback-user.tooltip")}
            required={enabled}
          >
            <FormSelect<JansenHuybregtsFormValues, UserDto>
              items={adminUsers}
              renderOption={(user) => `${user.firstName} ${user.lastName}`}
              keySelector={(user) => user.id}
              emptyItem={t("page.integration.jansen-huybregts.form.select-user")}
              name="fallbackUser"
              disabled={!enabled}
              rules={{
                validate: {
                  required: (value) =>
                    !enabled || value ? undefined : t("page.integration.jansen-huybregts.form.fallback-user.required"),
                },
              }}
            />
          </FormField>

          <FormField
            label={t("page.integration.jansen-huybregts.form.assignee-user")}
            description={t("page.integration.jansen-huybregts.form.assignee-user.description")}
            tooltip={t("page.integration.jansen-huybregts.form.assignee-user.tooltip")}
          >
            <FormSelect<JansenHuybregtsFormValues, UserDto>
              items={adminUsers}
              renderOption={(user) => `${user.firstName} ${user.lastName}`}
              keySelector={(user) => user.id}
              emptyItem={t("page.integration.jansen-huybregts.form.none")}
              name="assigneeUser"
              disabled={!enabled}
            />
          </FormField>

          <FormField
            label={t("page.integration.jansen-huybregts.form.requester-user")}
            tooltip={t("page.integration.jansen-huybregts.form.requester-user.tooltip")}
            required={enabled}
          >
            <FormSelect<JansenHuybregtsFormValues, UserDto>
              items={adminUsers}
              renderOption={(user) => `${user.firstName} ${user.lastName}`}
              keySelector={(user) => user.id}
              emptyItem={t("page.integration.jansen-huybregts.form.select-user")}
              name="requesterUser"
              disabled={!enabled}
              rules={{
                validate: {
                  required: (value) =>
                    !enabled || value ? undefined : t("page.integration.jansen-huybregts.form.requester-user.required"),
                },
              }}
            />
          </FormField>

          <FormCheckbox<JansenHuybregtsFormValues, "ignoreCategories">
            name="ignoreCategories"
            label={t("page.integration.jansen-huybregts.form.ignore-categories")}
            tooltip={t("page.integration.jansen-huybregts.form.ignore-categories.tooltip")}
            disabled={!enabled}
          />

          {enabled && (
            <JansenHuybregtsStatusMappings ticketStatusOptions={ticketStatusOptions} isSubmitting={isSubmitting} />
          )}
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}

function JansenHuybregtsStatusMappings({
  ticketStatusOptions,
  isSubmitting,
}: {
  ticketStatusOptions: JansenHuybregtsTicketStatusOptionsDto;
  isSubmitting: boolean;
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Accordion.Root>
      <Accordion.Item value="status-mappings">
        <Accordion.Trigger>{t("page.integration.jansen-huybregts.form.status-mappings")}</Accordion.Trigger>
        <Accordion.Content>
          <Stack gap="sm">
            <p className="text-caption text-text-secondary">
              {t("page.integration.jansen-huybregts.form.status-mappings.tooltip")}
            </p>
            {ticketStatusOptions.externalStatuses.map((externalStatus, index) => (
              <FormField
                key={externalStatus.value}
                label={externalStatus.label}
                tooltip={t("page.integration.jansen-huybregts.form.status-mappings.row.tooltip", {
                  status: externalStatus.label,
                })}
              >
                <FormSelect<JansenHuybregtsFormValues, JansenHuybregtsAopTicketStatusOptionDto>
                  items={ticketStatusOptions.aopStatuses}
                  keySelector={(status) => status.id}
                  renderOption={(status) => (
                    <TicketStatus description={status.label} labelColor={status.color} variant="dot" />
                  )}
                  renderSelected={(status) => (
                    <TicketStatus description={status.label} labelColor={status.color} variant="dot" />
                  )}
                  name={`statusMappings.${index}.aopStatus`}
                  emptyItem={t("page.integration.jansen-huybregts.form.status-mappings.none")}
                  disabled={isSubmitting}
                />
              </FormField>
            ))}
          </Stack>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
}
